import { concatBytes, toUint8Array } from './bytes.js';

export const AES_256_GCM_KEY_LENGTH_BYTES = 32;
export const AES_GCM_NONCE_LENGTH_BYTES = 12;
export const AES_GCM_TAG_LENGTH_BYTES = 16;

function getSubtleCrypto() {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('WebCrypto subtle API is unavailable.');
  }
  return subtle;
}

function assertLength(bytes, expectedLength, fieldName) {
  if (bytes.length !== expectedLength) {
    throw new Error(`${fieldName} must be ${expectedLength} bytes.`);
  }
}

async function importAesKey(keyBytes, usages) {
  const subtle = getSubtleCrypto();
  return subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    usages
  );
}

export async function encryptAes256Gcm({
  keyBytes,
  plaintextBytes,
  nonceBytes,
  aadBytes = new Uint8Array(),
}) {
  const key = toUint8Array(keyBytes, 'keyBytes');
  const nonce = toUint8Array(nonceBytes, 'nonceBytes');
  const plaintext = toUint8Array(plaintextBytes, 'plaintextBytes');
  const aad = toUint8Array(aadBytes, 'aadBytes');

  assertLength(key, AES_256_GCM_KEY_LENGTH_BYTES, 'keyBytes');
  assertLength(nonce, AES_GCM_NONCE_LENGTH_BYTES, 'nonceBytes');

  const subtle = getSubtleCrypto();
  const cryptoKey = await importAesKey(key, ['encrypt']);
  const encrypted = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      additionalData: aad,
      tagLength: AES_GCM_TAG_LENGTH_BYTES * 8,
    },
    cryptoKey,
    plaintext
  );

  const combined = new Uint8Array(encrypted);
  const splitIndex = combined.length - AES_GCM_TAG_LENGTH_BYTES;
  const ciphertextBytes = combined.slice(0, splitIndex);
  const tagBytes = combined.slice(splitIndex);

  return { ciphertextBytes, tagBytes };
}

export async function decryptAes256Gcm({
  keyBytes,
  nonceBytes,
  aadBytes = new Uint8Array(),
  ciphertextBytes,
  tagBytes,
}) {
  const key = toUint8Array(keyBytes, 'keyBytes');
  const nonce = toUint8Array(nonceBytes, 'nonceBytes');
  const aad = toUint8Array(aadBytes, 'aadBytes');
  const ciphertext = toUint8Array(ciphertextBytes, 'ciphertextBytes');
  const tag = toUint8Array(tagBytes, 'tagBytes');

  assertLength(key, AES_256_GCM_KEY_LENGTH_BYTES, 'keyBytes');
  assertLength(nonce, AES_GCM_NONCE_LENGTH_BYTES, 'nonceBytes');
  assertLength(tag, AES_GCM_TAG_LENGTH_BYTES, 'tagBytes');

  const subtle = getSubtleCrypto();
  const cryptoKey = await importAesKey(key, ['decrypt']);
  const combined = concatBytes(ciphertext, tag);

  try {
    const decrypted = await subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        additionalData: aad,
        tagLength: AES_GCM_TAG_LENGTH_BYTES * 8,
      },
      cryptoKey,
      combined
    );
    return new Uint8Array(decrypted);
  } catch {
    throw new Error('Failed to decrypt data.');
  }
}
